import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import {
  defaultCabinaSettings,
  slowModeOptions,
  maxMensajesOptions,
} from '@/mocks/adminCabinaSettings';
import type { CabinaSettingsConfig } from '@/mocks/adminCabinaSettings';

type PageState = 'loading' | 'error' | 'empty' | 'ready';
type SaveState = 'idle' | 'saving' | 'saved';

export default function AdminCabinaSettingsPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [settings, setSettings] = useState<CabinaSettingsConfig>(defaultCabinaSettings);
  const [originalSettings, setOriginalSettings] = useState<CabinaSettingsConfig>(defaultCabinaSettings);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [newWord, setNewWord] = useState('');
  const [wordError, setWordError] = useState('');

  const loadSettings = useCallback(() => {
    setPageState('loading');
    setTimeout(() => {
      const shouldFail = Math.random() < 0.08;
      const shouldBeEmpty = Math.random() < 0.05;

      if (shouldFail) {
        setPageState('error');
      } else if (shouldBeEmpty) {
        setSettings({ ...defaultCabinaSettings, activarCabina: false, mensajesAnonimos: false, requiereRegistro: false, slowMode: false, autoModeracion: false, autoRespuesta: false, palabrasProhibidas: [], mensajeBienvenida: '', mensajeFueraHorario: '', mensajeAutoRespuesta: '', maxMensajesPorUsuario: 0 });
        setOriginalSettings({ ...defaultCabinaSettings, activarCabina: false, mensajesAnonimos: false, requiereRegistro: false, slowMode: false, autoModeracion: false, autoRespuesta: false, palabrasProhibidas: [], mensajeBienvenida: '', mensajeFueraHorario: '', mensajeAutoRespuesta: '', maxMensajesPorUsuario: 0 });
        setPageState('empty');
      } else {
        setSettings({ ...defaultCabinaSettings });
        setOriginalSettings({ ...defaultCabinaSettings });
        setPageState('ready');
      }
    }, 850);
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  const handleToggle = (field: keyof CabinaSettingsConfig) => {
    if (typeof settings[field] === 'boolean') {
      setSettings((prev) => ({ ...prev, [field]: !prev[field] }));
    }
  };

  const handleChange = (field: keyof CabinaSettingsConfig, value: string | number) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!hasChanges) {
      await SwalAlert.toast('No hay cambios para guardar.', 'info');
      return;
    }

    setSaveState('saving');

    setTimeout(async () => {
      setSaveState('saved');
      setOriginalSettings({ ...settings });
      await SwalAlert.toast('Configuración guardada correctamente.', 'success');
      setTimeout(() => setSaveState('idle'), 1800);
    }, 700);
  };

  const handleCancel = async () => {
    if (!hasChanges) return;

    const result = await SwalAlert.confirm(
      '¿Descartar cambios?',
      'Se perderán todas las modificaciones que hiciste. Esta acción no se puede deshacer.',
    );

    if (result.isConfirmed) {
      setSettings({ ...originalSettings });
      await SwalAlert.toast('Cambios descartados.', 'info');
    }
  };

  const handleRestore = async () => {
    const result = await SwalAlert.confirm(
      '¿Restaurar valores por defecto?',
      'Se reemplazará toda la configuración actual con los valores predeterminados de fábrica. Perderás tus cambios actuales.',
    );

    if (result.isConfirmed) {
      setSettings({ ...defaultCabinaSettings });
      setOriginalSettings({ ...defaultCabinaSettings });
      await SwalAlert.toast('Valores restaurados correctamente.', 'success');
    }
  };

  const handleAddWord = () => {
    const word = newWord.trim().toLowerCase();
    if (!word) {
      setWordError('Escribí una palabra.');
      return;
    }
    if (word.length < 2) {
      setWordError('Mínimo 2 caracteres.');
      return;
    }
    if (settings.palabrasProhibidas.includes(word)) {
      setWordError('Esa palabra ya está en la lista.');
      return;
    }
    setSettings((prev) => ({
      ...prev,
      palabrasProhibidas: [...prev.palabrasProhibidas, word],
    }));
    setNewWord('');
    setWordError('');
  };

  const handleRemoveWord = async (word: string) => {
    const result = await SwalAlert.confirm(
      '¿Eliminar palabra?',
      `Se eliminará "${word}" de la lista de palabras prohibidas.`,
    );
    if (result.isConfirmed) {
      setSettings((prev) => ({
        ...prev,
        palabrasProhibidas: prev.palabrasProhibidas.filter((w) => w !== word),
      }));
      await SwalAlert.toast(`"${word}" eliminada.`, 'info');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddWord();
    }
  };

  const saveBtnLabel = saveState === 'saving' ? 'Guardando...' : saveState === 'saved' ? '¡Guardado!' : 'Guardar';
  const saveBtnClass = saveState === 'saved'
    ? 'bg-emerald-500 text-background-50 hover:bg-emerald-600'
    : 'bg-primary-500 text-background-50 hover:bg-primary-600';

  /* ─── Loading ─── */
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-48 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 pb-8 max-w-[800px] mx-auto space-y-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-5 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="h-4 w-40 rounded-md bg-background-200"></div>
                      <div className="h-3 w-64 rounded-md bg-background-200"></div>
                    </div>
                    <div className="w-12 h-6 rounded-full bg-background-200"></div>
                  </div>
                </div>
              ))}
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Error ─── */
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
              <i className="ri-error-warning-line text-background-50 text-xs"></i>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950 mb-1">Error al cargar configuración</h2>
              <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
                No se pudo cargar la configuración de la cabina. Verificá tu conexión e intentá de nuevo.
              </p>
              <button
                onClick={loadSettings}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line text-base"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Empty ─── */
  if (pageState === 'empty') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-secondary-500 flex items-center justify-center">
                <i className="ri-settings-3-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Config. Cabina</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-4">
                <i className="ri-settings-3-line text-foreground-400 text-2xl"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950 mb-1">Sin configuración</h2>
              <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
                No se encontró configuración guardada. Restaurá los valores por defecto para empezar.
              </p>
              <button
                onClick={handleRestore}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line text-base"></i>
                Restaurar valores
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Ready ─── */
  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-secondary-500 flex items-center justify-center">
              <i className="ri-settings-3-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Config. Cabina</h1>
              <p className="text-foreground-400 text-[9px]">{settings.activarCabina ? 'Activada' : 'Desactivada'}</p>
            </div>
          </div>
          <Link
            to="/admin/cabina"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Cabina</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/cabina"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                Cabina
              </Link>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Configuración de Cabina</h2>
                <p className="text-foreground-400 text-xs">
                  {settings.activarCabina ? 'Cabina activada' : 'Cabina desactivada'} &bull; {settings.palabrasProhibidas.length} palabras filtradas
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[750px] mx-auto space-y-5">
              {/* Status bar */}
              <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${
                settings.activarCabina
                  ? 'bg-emerald-50/60 border-emerald-200'
                  : 'bg-foreground-100 border-foreground-200'
              }`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                  settings.activarCabina ? 'bg-emerald-100' : 'bg-foreground-200'
                }`}>
                  <i className={`${settings.activarCabina ? 'ri-broadcast-line text-emerald-600' : 'ri-stop-circle-line text-foreground-500'} text-lg`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${settings.activarCabina ? 'text-emerald-800' : 'text-foreground-600'}`}>
                    Cabina {settings.activarCabina ? 'activada' : 'desactivada'}
                  </p>
                  <p className="text-[11px] text-foreground-500">
                    {settings.activarCabina
                      ? 'Los oyentes pueden enviar mensajes. La moderación está activa.'
                      : 'Los oyentes no pueden enviar mensajes. Activá la cabina para recibir mensajes.'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={settings.activarCabina}
                    onChange={() => handleToggle('activarCabina')}
                    className="sr-only"
                  />
                  <div className={`w-12 h-6 rounded-full transition-colors ${settings.activarCabina ? 'bg-emerald-500' : 'bg-foreground-300'}`}></div>
                  <div className={`absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-background-50 transition-transform ${settings.activarCabina ? 'translate-x-6' : ''}`}></div>
                </label>
              </div>

              {/* ===== FUNCIONES: Mensajes anónimos + Requiere registro ===== */}
              <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-background-100">
                  <h3 className="text-sm font-bold text-foreground-900">Funciones de mensajería</h3>
                  <p className="text-[10px] text-foreground-400 mt-0.5">Controlá quién puede enviar mensajes y cómo</p>
                </div>

                {/* Mensajes anónimos */}
                <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                      <i className="ri-eye-off-line text-violet-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Mensajes anónimos</p>
                      <p className="text-[11px] text-foreground-500">Permitir que los oyentes manden mensajes sin mostrar su nombre</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input type="checkbox" checked={settings.mensajesAnonimos} onChange={() => handleToggle('mensajesAnonimos')} className="sr-only" />
                    <div className={`w-10 h-5 rounded-full transition-colors ${settings.mensajesAnonimos ? 'bg-violet-500' : 'bg-foreground-300'}`}></div>
                    <div className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${settings.mensajesAnonimos ? 'translate-x-5' : ''}`}></div>
                  </label>
                </div>

                {/* Requiere registro */}
                <div className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-user-add-line text-primary-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Requiere registro</p>
                      <p className="text-[11px] text-foreground-500">Solo usuarios registrados pueden enviar mensajes a la cabina</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input type="checkbox" checked={settings.requiereRegistro} onChange={() => handleToggle('requiereRegistro')} className="sr-only" />
                    <div className={`w-10 h-5 rounded-full transition-colors ${settings.requiereRegistro ? 'bg-primary-500' : 'bg-foreground-300'}`}></div>
                    <div className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${settings.requiereRegistro ? 'translate-x-5' : ''}`}></div>
                  </label>
                </div>
              </div>

              {/* ===== FUNCIONES: Slow Mode + Auto moderación + Auto respuesta ===== */}
              <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-background-100">
                  <h3 className="text-sm font-bold text-foreground-900">Control de flujo</h3>
                  <p className="text-[10px] text-foreground-400 mt-0.5">Moderación automática y ritmo de mensajes</p>
                </div>

                {/* Slow Mode */}
                <div className="px-5 py-4 border-b border-background-100">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                        <i className="ri-timer-line text-amber-600 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">Slow Mode</p>
                        <p className="text-[11px] text-foreground-500">Limita la frecuencia con que un usuario puede enviar mensajes</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input type="checkbox" checked={settings.slowMode} onChange={() => handleToggle('slowMode')} className="sr-only" />
                      <div className={`w-10 h-5 rounded-full transition-colors ${settings.slowMode ? 'bg-amber-500' : 'bg-foreground-300'}`}></div>
                      <div className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${settings.slowMode ? 'translate-x-5' : ''}`}></div>
                    </label>
                  </div>
                  {settings.slowMode && (
                    <div className="mt-3 ml-12 flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide whitespace-nowrap">Tiempo:</span>
                      <select
                        value={settings.slowModeSegundos}
                        onChange={(e) => handleChange('slowModeSegundos', Number(e.target.value))}
                        className="px-3 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-[11px] font-semibold text-foreground-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400/60 transition-all"
                      >
                        {slowModeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <span className="text-[10px] text-foreground-400">entre mensajes</span>
                    </div>
                  )}
                </div>

                {/* Auto moderación */}
                <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                      <i className="ri-shield-check-line text-red-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Auto moderación</p>
                      <p className="text-[11px] text-foreground-500">Filtra automáticamente mensajes con palabras prohibidas y spam</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input type="checkbox" checked={settings.autoModeracion} onChange={() => handleToggle('autoModeracion')} className="sr-only" />
                    <div className={`w-10 h-5 rounded-full transition-colors ${settings.autoModeracion ? 'bg-red-500' : 'bg-foreground-300'}`}></div>
                    <div className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${settings.autoModeracion ? 'translate-x-5' : ''}`}></div>
                  </label>
                </div>

                {/* Auto respuesta */}
                <div className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                      <i className="ri-robot-line text-emerald-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Auto respuesta</p>
                      <p className="text-[11px] text-foreground-500">Responde automáticamente a cada mensaje recibido con un texto predefinido</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input type="checkbox" checked={settings.autoRespuesta} onChange={() => handleToggle('autoRespuesta')} className="sr-only" />
                    <div className={`w-10 h-5 rounded-full transition-colors ${settings.autoRespuesta ? 'bg-emerald-500' : 'bg-foreground-300'}`}></div>
                    <div className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${settings.autoRespuesta ? 'translate-x-5' : ''}`}></div>
                  </label>
                </div>
              </div>

              {/* ===== CONFIGURACIÓN: Mensajes de texto ===== */}
              <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-background-100">
                  <h3 className="text-sm font-bold text-foreground-900">Mensajes automáticos</h3>
                  <p className="text-[10px] text-foreground-400 mt-0.5">Textos que ven los oyentes en distintas situaciones</p>
                </div>

                {/* Mensaje bienvenida */}
                <div className="px-5 py-4 border-b border-background-100">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
                      <i className="ri-hand-heart-line text-sky-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Mensaje de bienvenida</p>
                      <p className="text-[11px] text-foreground-500">Se muestra cuando un oyente abre la cabina por primera vez</p>
                    </div>
                  </div>
                  <textarea
                    value={settings.mensajeBienvenida}
                    onChange={(e) => handleChange('mensajeBienvenida', e.target.value)}
                    maxLength={300}
                    rows={3}
                    placeholder="Escribí el mensaje de bienvenida..."
                    className="w-full p-3 rounded-xl border border-background-200 bg-background-50 text-xs text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-sky-400/60 focus:border-sky-400 resize-none transition-all"
                  />
                  <p className="text-[9px] text-foreground-400 mt-1 text-right">{settings.mensajeBienvenida.length}/300</p>
                </div>

                {/* Mensaje fuera de horario */}
                <div className="px-5 py-4 border-b border-background-100">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
                      <i className="ri-moon-line text-rose-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Mensaje fuera de horario</p>
                      <p className="text-[11px] text-foreground-500">Se muestra cuando la cabina está cerrada (fuera del horario de programas)</p>
                    </div>
                  </div>
                  <textarea
                    value={settings.mensajeFueraHorario}
                    onChange={(e) => handleChange('mensajeFueraHorario', e.target.value)}
                    maxLength={300}
                    rows={3}
                    placeholder="Escribí el mensaje de fuera de horario..."
                    className="w-full p-3 rounded-xl border border-background-200 bg-background-50 text-xs text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-rose-400/60 focus:border-rose-400 resize-none transition-all"
                  />
                  <p className="text-[9px] text-foreground-400 mt-1 text-right">{settings.mensajeFueraHorario.length}/300</p>
                </div>

                {/* Auto respuesta mensaje */}
                <div className="px-5 py-4">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                      <i className="ri-robot-line text-emerald-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Texto de auto respuesta</p>
                      <p className="text-[11px] text-foreground-500">Mensaje automático que recibe cada oyente al enviar un mensaje (si Auto respuesta está activo)</p>
                    </div>
                  </div>
                  <textarea
                    value={settings.mensajeAutoRespuesta}
                    onChange={(e) => handleChange('mensajeAutoRespuesta', e.target.value)}
                    maxLength={300}
                    rows={3}
                    placeholder="Escribí el mensaje de auto respuesta..."
                    className="w-full p-3 rounded-xl border border-background-200 bg-background-50 text-xs text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-emerald-400/60 focus:border-emerald-400 resize-none transition-all"
                  />
                  <p className="text-[9px] text-foreground-400 mt-1 text-right">{settings.mensajeAutoRespuesta.length}/300</p>
                </div>
              </div>

              {/* ===== CONFIGURACIÓN: Palabras prohibidas + Máx mensajes ===== */}
              <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-background-100">
                  <h3 className="text-sm font-bold text-foreground-900">Restricciones</h3>
                  <p className="text-[10px] text-foreground-400 mt-0.5">Límites y filtros de contenido</p>
                </div>

                {/* Palabras prohibidas */}
                <div className="px-5 py-4 border-b border-background-100">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                      <i className="ri-file-text-line text-red-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Palabras prohibidas</p>
                      <p className="text-[11px] text-foreground-500">Los mensajes que contengan estas palabras serán filtrados automáticamente</p>
                    </div>
                  </div>

                  {/* Word input */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={newWord}
                        onChange={(e) => { setNewWord(e.target.value); setWordError(''); }}
                        onKeyDown={handleKeyDown}
                        placeholder="Agregar palabra..."
                        maxLength={30}
                        className={`w-full pl-3 pr-3 py-2 rounded-xl border text-xs text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 transition-all ${
                          wordError ? 'border-red-300 bg-red-50 focus:ring-red-400/60' : 'border-background-200 bg-background-50 focus:ring-primary-400/60'
                        }`}
                      />
                    </div>
                    <button
                      onClick={handleAddWord}
                      className="px-4 py-2 rounded-xl bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-add-line text-sm mr-1"></i>
                      Agregar
                    </button>
                  </div>
                  {wordError && <p className="text-[10px] text-red-500 -mt-2 mb-2 ml-12">{wordError}</p>}

                  {/* Word tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {settings.palabrasProhibidas.length === 0 && (
                      <p className="text-[11px] text-foreground-400 italic">No hay palabras en la lista.</p>
                    )}
                    {settings.palabrasProhibidas.map((word) => (
                      <span
                        key={word}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-50 border border-red-200 text-[10px] font-semibold text-red-700"
                      >
                        {word}
                        <button
                          onClick={() => handleRemoveWord(word)}
                          className="w-4 h-4 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-200 transition-colors"
                        >
                          <i className="ri-close-line text-[10px]"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Máx mensajes por usuario */}
                <div className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                      <i className="ri-chat-1-line text-orange-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Mensajes máximos por usuario</p>
                      <p className="text-[11px] text-foreground-500">Límite de mensajes que un usuario puede enviar por sesión</p>
                    </div>
                  </div>
                  <select
                    value={settings.maxMensajesPorUsuario}
                    onChange={(e) => handleChange('maxMensajesPorUsuario', Number(e.target.value))}
                    className="px-3 py-2 rounded-xl border border-background-200 bg-background-50 text-xs font-semibold text-foreground-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all shrink-0"
                  >
                    {maxMensajesOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ===== BOTONES: Guardar, Cancelar, Restaurar ===== */}
              <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saveState !== 'idle'}
                    className={`flex items-center justify-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex-1 sm:flex-none ${saveBtnClass}`}
                  >
                    {saveState === 'saving' ? (
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                    ) : saveState === 'saved' ? (
                      <i className="ri-check-line text-sm"></i>
                    ) : (
                      <i className="ri-save-line text-sm"></i>
                    )}
                    {saveBtnLabel}
                  </button>

                  <button
                    onClick={handleCancel}
                    disabled={!hasChanges}
                    className="flex items-center justify-center gap-1.5 px-6 py-2.5 rounded-xl border border-background-200 text-foreground-600 text-sm font-semibold hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex-1 sm:flex-none"
                  >
                    <i className="ri-close-line text-sm"></i>
                    Cancelar
                  </button>

                  <button
                    onClick={handleRestore}
                    className="flex items-center justify-center gap-1.5 px-6 py-2.5 rounded-xl bg-foreground-100 text-foreground-600 text-sm font-semibold hover:bg-foreground-200 cursor-pointer transition-colors whitespace-nowrap ml-auto flex-1 sm:flex-none"
                  >
                    <i className="ri-refresh-line text-sm"></i>
                    Restaurar valores
                  </button>
                </div>
                {hasChanges && (
                  <p className="text-[10px] text-amber-600 mt-3 flex items-center gap-1">
                    <i className="ri-error-warning-line text-[11px]"></i>
                    Tenés cambios sin guardar.
                  </p>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>


    </div>
  );
}